/* Scented Lumos BG — cart guards + product add-ons ("Добави към подаръка").

   Loaded on every page from layout/theme.liquid, after Ecomify's cart.js.
   Markup for the add-ons: snippets/sl-pdp-addons.liquid (rows picked
   server-side).

   Every page:
   - Express production (SKU express-bg) is one line, qty 1, never alone.
     After every Ecomify cart change, a cart holding only express loses it and
     qty > 1 goes back to 1. The cart drawer hides that line, so nobody else
     would notice.

   Product pages (the main product form only):
   - The main add-to-cart runs here instead of in Ecomify's onSubmitAtcForm
     (other forms — quick-add cards, the upsell modal, "Комбинирай с" — still
     go to Ecomify). Same request as Ecomify: multipart FormData of the form,
     so personalisation text and photo uploads travel exactly as before. Owning
     the request is what makes the rest safe: the add-ons are posted only
     after THIS response is OK, a second click while it is in flight is
     ignored (the sticky bar clicks the same button), and buy-now redirects
     only on this response.
   - Add-ons: one /cart/add.js JSON call, qty 1, no properties, after a fresh
     /cart.js check that drops express if already there and any product
     already in the cart.
   - "Купи сега": Ecomify sends it to /cart/<variant>:<qty>, a permalink that
     carries no line-item properties (typed personalisation, uploaded photos)
     and no add-ons. When the form holds any property value or an add-on is
     on, the click goes through the main add above and then to /checkout.
     Otherwise Ecomify's permalink runs untouched.
   - Rows whose product is already in the cart are swapped for that family's
     backup row. */
(() => {
  'use strict';
  if (window.SLAddons) return;

  const EXPRESS_SKU = 'express-bg';
  const SERVICE_SKUS = new Set([EXPRESS_SKU]);
  const TIMEOUT = 8000;
  const BUY_NOW_TTL = 20000;
  const SPINNER = '<span class="spinner-border spinner-border-sm" role="status"><span class="visually-hidden">…</span></span>';
  const S = {
    inCart: 'Вече е в количката',
    error: 'Не успяхме да добавим всичко. Опитай отново.',
    network: 'Връзката прекъсна. Провери количката, преди да опиташ отново.',
  };

  const skuOf = (item) => String((item && item.sku) || '').toLowerCase();
  const isService = (item) => SERVICE_SKUS.has(skuOf(item));
  const mainForm = () => document.querySelector('#product-template #product-content > form[action*="/cart/add"]');
  const box = () => {
    const form = mainForm();
    return form ? form.querySelector('[data-sl-addons]') : null;
  };

  function timedFetch(url, options = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT);
    return fetch(url, Object.assign({}, options, { signal: controller.signal })).finally(() => clearTimeout(timer));
  }

  async function getCart() {
    const res = await timedFetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    return res.json();
  }

  async function postJson(url, body) {
    const res = await timedFetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = new Error('HTTP ' + res.status);
      err.status = res.status;
      throw err;
    }
    return res;
  }

  // ---------- express: one line, qty 1, never alone (every page) ----------

  let reconciling = false;
  let dirty = false;
  let reruns = 0;
  async function reconcile() {
    if (reconciling) { dirty = true; return; }
    reconciling = true;
    try {
      // A cart change that lands mid-pass sets `dirty` and gets its own pass.
      for (let pass = 0; pass < 3; pass += 1) {
        dirty = false;
        const cart = await getCart();
        const express = (cart.items || []).filter((i) => skuOf(i) === EXPRESS_SKU);
        const hasProducts = (cart.items || []).some((i) => !isService(i));
        const updates = {};
        express.forEach((line, index) => {
          const want = hasProducts && index === 0 ? 1 : 0;
          if (line.quantity !== want) updates[line.key] = want;
        });
        if (Object.keys(updates).length) {
          const response = await postJson('/cart/update.js', { updates });
          if (typeof window.refreshCartContents === 'function') await window.refreshCartContents(response);
        }
        if (!dirty) break;
      }
    } catch (_) {
      /* the next cart change retries */
    } finally {
      reconciling = false;
      // A change that landed during the last pass still gets looked at
      // (bounded, so a cart that never settles can't loop forever).
      if (dirty && reruns < 5) {
        reruns += 1;
        setTimeout(reconcile, 300);
      } else {
        reruns = 0;
      }
    }
  }

  window.addEventListener('updated.ecomify.cart', () => { reconcile(); });
  reconcile();

  // ---------- product page ----------

  const selected = (b) => (b ? [...b.querySelectorAll('[data-sl-addon]:checked:not(:disabled)')] : []);

  function syncSelected() {
    document.body.classList.toggle('sl-addons-on', selected(box()).length > 0);
  }

  function setStatus(text) {
    const el = box() && box().querySelector('[data-sl-addons-status]');
    if (!el) return;
    el.textContent = text || '';
    el.hidden = !text;
  }

  function markExpress(b, inCart) {
    const toggle = b.querySelector('[data-sl-addon-express]');
    if (!toggle) return;
    toggle.checked = inCart;
    toggle.disabled = inCart;
    const note = toggle.closest('[data-sl-addon-express-row]').querySelector('[data-sl-addon-note]');
    if (note) {
      if (!note.dataset.slNote) note.dataset.slNote = note.textContent;
      note.textContent = inCart ? S.inCart : note.dataset.slNote;
    }
  }

  // One row per family, in DOM order, skipping products already in the cart.
  function layoutRows(b, cart) {
    const inCart = new Set((cart.items || []).map((i) => String(i.product_id)));
    const max = Number(b.dataset.max || 3);
    const used = new Set();
    let shown = 0;
    b.querySelectorAll('[data-sl-addon-row]').forEach((row) => {
      const input = row.querySelector('[data-sl-addon]');
      const ok = !inCart.has(row.dataset.productId) && !used.has(row.dataset.family) && shown < max;
      if (ok) {
        used.add(row.dataset.family);
        shown += 1;
      } else if (input) {
        input.checked = false;
      }
      row.hidden = !ok;
    });
    markExpress(b, (cart.items || []).some((i) => skuOf(i) === EXPRESS_SKU));
    b.hidden = !b.querySelector('[data-sl-addon-row]:not([hidden]), [data-sl-addon-express-row]');
    syncSelected();
  }

  async function refreshRows() {
    const b = box();
    if (!b) return;
    try {
      layoutRows(b, await getCart());
    } catch (_) { /* keep the server-rendered rows */ }
  }

  function captureAddons() {
    return selected(box()).map((input) => {
      const row = input.closest('[data-sl-addon-row]');
      return {
        id: Number(input.dataset.variantId),
        productId: row ? String(row.dataset.productId) : '',
        express: input.hasAttribute('data-sl-addon-express'),
      };
    });
  }

  // Returns how many add-ons did not make it.
  async function addAddons(job) {
    let items;
    try {
      const cart = await getCart();
      const inCart = new Set((cart.items || []).map((i) => String(i.product_id)));
      const hasExpress = (cart.items || []).some((i) => skuOf(i) === EXPRESS_SKU);
      items = job.filter((i) => (i.express ? !hasExpress : !inCart.has(i.productId)));
    } catch (_) {
      // Can't see the cart, so can't rule out a second copy: add nothing.
      return job.length;
    }
    const payload = items.map((i) => ({ id: i.id, quantity: 1 }));
    if (!payload.length) return 0;
    try {
      await postJson('/cart/add.js', { items: payload });
      return 0;
    } catch (err) {
      // Only a validation answer (422) proves nothing landed; anything else
      // may have, so never re-post blind.
      if (err.status !== 422) return payload.length;
      let failed = 0;
      for (const item of payload) {
        try { await postJson('/cart/add.js', { items: [item] }); } catch (_) { failed += 1; }
      }
      return failed;
    }
  }

  let inFlight = false;
  let buyNow = null;

  function releaseBuyNow() {
    if (!buyNow) return;
    clearTimeout(buyNow.timer);
    buyNow.button.disabled = false;
    buyNow.button.innerHTML = buyNow.label;
    buyNow = null;
  }

  // Back/forward cache can bring the page back mid-submit (e.g. from
  // checkout): never resume with a locked button.
  let restoreMain = null;
  function resetSubmitState() {
    if (restoreMain) restoreMain();
    restoreMain = null;
    releaseBuyNow();
    inFlight = false;
  }

  function showDrawer() {
    const drawer = document.querySelector('#offcanvas-cart');
    if (drawer && window.bootstrap && window.bootstrap.Offcanvas) window.bootstrap.Offcanvas.getOrCreateInstance(drawer).show();
  }

  // Ecomify's onSubmitAtcForm, for the main form, plus the add-ons.
  async function submitMain(form, event) {
    if (event && typeof event.preventDefault === 'function') event.preventDefault();
    if (inFlight) return;
    inFlight = true;
    const buy = buyNow;
    const btn = form.querySelector('.btn-atc');
    const label = btn ? btn.innerHTML : '';
    const job = captureAddons();
    const inModal = !!(btn && btn.closest('.modal'));
    const upsellModal = form.hasAttribute('data-ecomify-upsell-modal');
    const drawer = document.querySelector('#offcanvas-cart');
    restoreMain = () => { if (btn) btn.innerHTML = label; form.classList.remove('loading'); };
    if (btn) btn.innerHTML = SPINNER;
    form.classList.add('loading');
    setStatus('');

    let response = null;
    try {
      let formData = null;
      // eslint-disable-next-line no-undef
      if (typeof getFormDataFromQtyBreaks === 'function') formData = await getFormDataFromQtyBreaks(form);
      if (!formData) formData = new FormData(form);
      response = await fetch('/cart/add.js', { method: 'POST', body: formData });
    } catch (_) { /* network error: outcome unknown, add nothing more */ }
    form.classList.remove('loading');

    if (!response) {
      // The request may still have landed. Show the real cart instead of
      // inviting a blind retry that could add the personalised item twice.
      resetSubmitState();
      setStatus(S.network);
      try {
        if (typeof window.refreshCartContents === 'function') await window.refreshCartContents(new Response(null, { status: 200 }));
      } catch (_) { /* drawer keeps its last state */ }
      showDrawer();
      return;
    }

    let failed = 0;
    if (response.ok && job.length) {
      if (drawer) drawer.classList.add('loading');
      try { failed = await addAddons(job); } catch (_) { failed = job.length; }
      const b = box();
      if (b) b.querySelectorAll('[data-sl-addon]:not([data-sl-addon-express])').forEach((i) => { i.checked = false; });
      syncSelected();
      setStatus(failed ? S.error : '');
    }

    // Only the buy-now that started this add, and only if it hasn't timed
    // out (the shopper may have edited the form since).
    if (buy && buyNow === buy) {
      // An add-on that didn't make it keeps the shopper here, drawer open.
      if (response.ok && !failed) {
        resetSubmitState();
        window.location.href = '/checkout';
        return;
      }
      releaseBuyNow();
    }

    if (btn) {
      if (response.ok) {
        btn.innerHTML = (window.theme && window.theme.product && window.theme.product.addedToCart) || label;
        setTimeout(() => { btn.innerHTML = label; }, 2000);
      } else {
        btn.innerHTML = label;
      }
    }

    const refreshing = Promise.resolve()
      .then(() => (typeof window.refreshCartContents === 'function' ? window.refreshCartContents(response) : null))
      .catch(() => {});
    if (!upsellModal && !inModal) showDrawer();
    if (response.ok) {
      const learnq = window._learnq || [];
      learnq.push(['track', 'Added to Cart', {
        ProductID: form.dataset.productId,
        Name: form.dataset.productTitle,
        URL: form.dataset.productUrl,
      }]);
    }
    await refreshing;
    restoreMain = null;
    inFlight = false;
  }

  function hasProperties(form) {
    return [...form.querySelectorAll('[name^="properties["]')].some((el) => {
      if (el.type === 'file') return !!(el.files && el.files.length);
      if (el.type === 'radio' || el.type === 'checkbox') return el.checked;
      // Hidden inputs count too: Ecomify's checkbox-style fields and app
      // personalisers write their value into one.
      return String(el.value || '').trim() !== '';
    });
  }

  function initProductPage() {
    // teeinblue products (flag set in layout/theme.liquid) add through their
    // own #teeAtcButton, which attaches the design: leave them to Ecomify.
    if (window.SL_PDP_SKIP) return;
    const form = mainForm();
    const original = window.onSubmitAtcForm;
    if (!form || typeof original !== 'function' || original.slWrapped) return;

    // The inline onsubmit="onSubmitAtcForm(this, event)" looks the name up at
    // call time, so this takes over the main form only.
    const wrapped = function (f, event) {
      if (f === mainForm()) return submitMain(f, event);
      return original.apply(this, arguments);
    };
    wrapped.slWrapped = true;
    window.onSubmitAtcForm = wrapped;

    document.addEventListener('click', (event) => {
      const button = event.target.closest && event.target.closest('.btn-buy');
      const f = mainForm();
      if (!button || !f || !f.contains(button)) return;
      if (!hasProperties(f) && !selected(box()).length) return;
      // Capture phase on document: Ecomify's inline onclick never runs.
      event.preventDefault();
      event.stopImmediatePropagation();
      const atc = f.querySelector('button.btn-atc');
      if (!atc || atc.disabled || inFlight || buyNow) return;
      if (typeof f.reportValidity === 'function' && !f.reportValidity()) return;
      buyNow = { button, label: button.innerHTML, timer: setTimeout(releaseBuyNow, BUY_NOW_TTL) };
      button.disabled = true;
      button.innerHTML = SPINNER;
      atc.click();
    }, true);

    document.addEventListener('change', (event) => {
      if (event.target.closest && event.target.closest('[data-sl-addons]')) syncSelected();
    });
    window.addEventListener('updated.ecomify.cart', () => { refreshRows(); });
    // Back/forward cache restores checked boxes; recount them.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) resetSubmitState();
      syncSelected();
      refreshRows();
    });
    document.addEventListener('shopify:section:load', () => { refreshRows(); });
    refreshRows();
  }

  // Ecomify's onChangeProductOption adds a "Спести" badge whenever a variant
  // HAS a compare-at price, even one below the price (121 SL BG variants on
  // 2026-10-09, mostly metal/cork mugs at €9.99 with compare-at €8.99), which
  // prints "Спести: €-1,00". Drop the badge unless compare-at is really higher.
  function guardSaleBadge() {
    const original = window.onChangeProductOption;
    if (typeof original !== 'function' || original.slWrapped) return;
    const wrapped = async function (input) {
      const result = await original.apply(this, arguments);
      try {
        const form = input && input.closest ? input.closest('form') : null;
        const idInput = form && form.querySelector('[name="id"]');
        const variants = form && form.dataset.variants ? JSON.parse(form.dataset.variants) : [];
        const variant = idInput && variants.find((v) => String(v.id) === String(idInput.value));
        const content = input.closest('.product-content');
        if (variant && !(variant.compare_at_price > variant.price) && content) {
          content.querySelectorAll('.product-price .price-badge-sale').forEach((badge) => badge.remove());
        }
      } catch (_) { /* leave Ecomify's output as is */ }
      return result;
    };
    wrapped.slWrapped = true;
    window.onChangeProductOption = wrapped;
  }

  // ---------- cart drawer: "Допълни подаръка" (every page) ----------
  // Rows come from snippets/sl-cart-addons.liquid, rebuilt by Ecomify after
  // every cart change. Switch on = add (qty 1, no properties). A row this
  // drawer added stays listed and switched on while its variant is in the
  // cart (sessionStorage, as on Gift Theory); switching it off removes that
  // plain line only, never a personalised one.
  const CART_KEY = 'slCartAddonsOn';
  const cartBox = () => document.querySelector('[data-sl-cart-addons]');
  function cartOn() {
    try { return JSON.parse(sessionStorage.getItem(CART_KEY) || '[]'); } catch (_) { return []; }
  }
  function saveCartOn(list) {
    try { sessionStorage.setItem(CART_KEY, JSON.stringify(list.slice(-6))); } catch (_) { /* private mode */ }
  }

  function paintCartAddons() {
    const box = cartBox();
    if (!box) return;
    const inCart = String(box.dataset.cartVariants || '');
    const list = box.querySelector('[data-sl-cart-addons-list]');
    const keep = cartOn().filter((r) => inCart.includes(',' + r.vid + ','));
    saveCartOn(keep);
    keep.slice().reverse().forEach((r) => {
      if (list.querySelector('[data-sl-cart-addon][data-variant-id="' + r.vid + '"]')) return;
      const holder = document.createElement('ul');
      holder.innerHTML = r.html;
      const li = holder.firstElementChild;
      if (!li) return;
      li.hidden = false;
      const input = li.querySelector('[data-sl-cart-addon]');
      if (input) input.checked = true;
      list.prepend(li);
    });
    box.hidden = !list.querySelector('li:not([hidden])');
  }

  let cartBusy = false;
  async function onCartAddonToggle(input) {
    if (cartBusy || inFlight) { input.checked = !input.checked; return; }
    cartBusy = true;
    const box = cartBox();
    if (box) box.setAttribute('data-busy', '');
    const want = input.checked;
    const vid = String(input.dataset.variantId);
    const row = input.closest('[data-sl-cart-addon-row]');
    let response = null;
    let failed = false;
    try {
      if (want) {
        response = await postJson('/cart/add.js', { items: [{ id: Number(vid), quantity: 1 }] });
        if (row) {
          const copy = row.cloneNode(true);
          copy.removeAttribute('hidden');
          saveCartOn(cartOn().filter((r) => r.vid !== vid).concat([{ vid, html: copy.outerHTML }]));
        }
      } else {
        saveCartOn(cartOn().filter((r) => r.vid !== vid));
        const cart = await getCart();
        const plain = (i) => !Object.keys(i.properties || {}).some((k) => k.charAt(0) !== '_' && i.properties[k]);
        const line = (cart.items || []).find((i) => String(i.variant_id) === vid && plain(i));
        if (line) response = await postJson('/cart/change.js', { id: line.key, quantity: 0 });
      }
    } catch (_) {
      failed = true;
      input.checked = !want;
    } finally {
      cartBusy = false;
      if (box) box.removeAttribute('data-busy');
    }
    if (failed) {
      const status = box && box.querySelector('[data-sl-cart-addons-status]');
      if (status) { status.textContent = S.error; status.hidden = false; }
      return;
    }
    // Ecomify repaints the drawer from the server (and fires its event,
    // which repaints the switched-on rows).
    if (typeof window.refreshCartContents === 'function') {
      try { await window.refreshCartContents(response || new Response(null, { status: 200 })); } catch (_) { /* next change repaints */ }
    }
  }

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (input && input.matches && input.matches('[data-sl-cart-addon]')) onCartAddonToggle(input);
  });
  window.addEventListener('updated.ecomify.cart', paintCartAddons);
  paintCartAddons();

  initProductPage();
  guardSaleBadge();
  window.SLAddons = { refreshRows, reconcile };
})();

/* Add-on photo zoom: tapping a row's photo opens it full size. Delegated,
   so it also covers rows the cart drawer re-renders. Esc, the × or a tap
   anywhere closes it. */
(() => {
  if (window.__slAddonZoom) return;
  window.__slAddonZoom = true;
  let overlay = null;
  let lastFocus = null;
  const close = () => {
    if (!overlay) return;
    overlay.remove();
    overlay = null;
    document.removeEventListener('keydown', onKey);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  };
  const onKey = (e) => { if (e.key === 'Escape') close(); };
  document.addEventListener('click', (event) => {
    const btn = event.target.closest && event.target.closest('[data-sl-addon-zoom]');
    if (!btn) return;
    event.preventDefault();
    event.stopPropagation();
    close();
    lastFocus = btn;
    overlay = document.createElement('div');
    overlay.className = 'sl-zoom';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', btn.getAttribute('data-sl-addon-zoom-alt') || '');
    const img = document.createElement('img');
    img.src = btn.getAttribute('data-sl-addon-zoom');
    img.alt = btn.getAttribute('data-sl-addon-zoom-alt') || '';
    const x = document.createElement('button');
    x.type = 'button';
    x.className = 'sl-zoom__close';
    x.setAttribute('aria-label', 'Затвори');
    x.textContent = '×';
    overlay.append(img, x);
    overlay.addEventListener('click', close);
    document.body.appendChild(overlay);
    document.addEventListener('keydown', onKey);
    x.focus();
  }, true);
})();

